import { useCallback, useEffect, useState } from 'react'

import { API_BASE } from '../../config'
import { DatePicker } from '../../lib/DatePicker'
import { monthRangeThai } from '../../lib/datetime'
import { errorText } from '../../lib/offsite'
import { Pager } from '../../lib/Pager'
import { SortBar, SortTh, useSort } from '../../lib/Sort'
import { useSession } from '../../lib/SessionContext'
import { SectionHead } from './parts'

/* ============================================================================
   ประวัติการลงเวลาผ่าน LINE — อ่านอย่างเดียว
   API: backend/src/routes/adminOffsite.js (/checkins) · lib/offsite.js → listCheckins

   ── ทำไมต้องมีหน้านี้ ──────────────────────────────────────────────────────
   รอยรูดจาก LIFF ถูกดันต่อเข้า HR_TA_DATA เพื่อให้เครื่องคำนวณเวลาทำงานเห็น
   (lib/checkin.js → pushToTa) แต่ตารางนั้นมี 8 คอลัมน์ **ไม่มีที่เก็บว่ามาจากไหน**
   → พอดันไปแล้วการลงเวลาจากบ้านหน้าตาเหมือนการสแกนหน้าที่ออฟฟิศทุกประการ

   ที่มาจริง (พิกัด · ระยะห่าง · รูปเซลฟี่ · เหตุที่ผ่านด่าน) อยู่ในตารางนี้ที่เดียว
   หน้านี้คือทางเดียวที่คนจะเห็นมันโดยไม่ต้องเขียน SQL

   ⚠️ สำคัญเป็นพิเศษกับกลุ่ม ProcessTA = 0 (ผู้บริหาร) เพราะ "ไม่เข้าออฟฟิศ" เป็นเรื่อง
      ปกติของเขา ไม่ใช่ข้อยกเว้น — อ่านรายงานย้อนหลังแล้วสรุปว่า "มาออฟฟิศ" ได้ง่ายมาก
   ============================================================================ */

type Checkin = {
  seq: number
  workDate: string
  /** เวลาไทย (รูปแบบ 'YYYY-MM-DD HH:mm:ss') — เก็บเป็นเวลาไทยในตาราง ไม่ใช่ UTC */
  at: string
  empCode: string
  empName: string | null
  kind: 'in' | 'out'
  lat: number | string | null
  lng: number | string | null
  accuracyM: number | null
  distanceM: number | null
  siteCode: string | null
  siteName: string | null
  allowReason: string | null
  allowMode: string | null
  note: string | null
  hasPhoto: number
  photoBytes: number | null
  /** ดันเข้า HR_TA_DATA แล้วหรือยัง — false ค้างนาน ๆ = เครื่องคำนวณยังไม่เห็นรอยนี้ */
  pushed: boolean
  taSeq: number | null
  /** 'Y' = ยกเลิกแล้ว (ไม่ถูกดันเข้าเครื่องคำนวณ) */
  cancelRec: string
}

type Paged<T> = { total: number; page: number; pageSize: number } & T

const BASE = '/api/admin/offsite'
const nf = new Intl.NumberFormat('th-TH')

const REASON_LABEL: Record<string, string> = {
  permission: 'ใบอนุญาตรายคน',
  window: 'ช่วงเปิดชั่วคราว',
}

export default function OffsiteCheckins() {
  const { call } = useSession()

  const [rows, setRows] = useState<Paged<{ checkins: Checkin[] }> | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const sort = useSort([{ key: 'at', dir: 'desc' }], () => setPage(1))

  /* ค่าเริ่มต้น = **วันที่ 1 ถึงสิ้นเดือน** ของเดือนปัจจุบัน
     รอบบัญชี/รอบเงินเดือนคิดเป็นเดือน การตรวจว่า "เดือนนี้ใครลงเวลาจากที่ไหนบ้าง"
     จึงเป็นคำถามที่คนเปิดหน้านี้ถามจริง ๆ ไม่ใช่ดูแค่วันนี้แล้วต้องกดเปลี่ยนทุกครั้ง

     ⚠️ `to` เป็นวันสิ้นเดือนซึ่งอาจอยู่ในอนาคต — ตั้งใจ ไม่ใช่บั๊ก การลงเวลาที่เกิด
        ในวันที่เหลือของเดือนจะโผล่มาเองโดยไม่ต้องแก้ตัวกรอง */
  const [range] = useState(() => monthRangeThai())
  const [from, setFrom] = useState(range.from)
  const [to, setTo] = useState(range.to)
  const [term, setTerm] = useState('')
  const [q, setQ] = useState('')
  const [offsiteOnly, setOffsiteOnly] = useState(false)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [photo, setPhoto] = useState<Checkin | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize) })
      if (from) qs.set('from', from)
      if (to) qs.set('to', to)
      if (q) qs.set('q', q)
      if (offsiteOnly) qs.set('offsiteOnly', 'true')
      if (sort.param) qs.set('sort', sort.param)
      setRows(await call<Paged<{ checkins: Checkin[] }>>(`${BASE}/checkins?${qs}`))
      setError(null)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoading(false)
    }
  }, [call, page, pageSize, from, to, q, offsiteOnly, sort.param])

  useEffect(() => {
    void load()
  }, [load])

  const list = rows?.checkins ?? []
  const pending = list.filter((c) => !c.pushed && c.cancelRec !== 'Y').length

  return (
    <>
      <h1>ประวัติการลงเวลาผ่าน LINE</h1>
      <p className="meta">
        รอยรูดจาก LINE ถูกส่งต่อเข้าตารางเดียวกับเครื่องสแกนหน้าเพื่อให้คิดเวลาทำงานได้ —
        แต่ตารางนั้น<strong>ไม่มีที่เก็บว่ารอยมาจากไหน</strong> หน้านี้คือที่เดียวที่เห็น
        พิกัด ระยะห่าง และรูปถ่ายตอนกด
      </p>

      {error && <p className="error">โหลดข้อมูลไม่สำเร็จ — {error}</p>}

      <section className="card">
        <SectionHead
          title="รายการลงเวลา"
          meta={rows ? `${nf.format(rows.total)} รายการ` : undefined}
          onReload={() => void load()}
          loading={loading}
        />

        <form
          className="toolbar"
          onSubmit={(e) => {
            e.preventDefault()
            setPage(1)
            setQ(term.trim())
          }}
        >
          <label>
            ตั้งแต่วันที่
            <DatePicker
              value={from}
              onChange={(v) => {
                setPage(1)
                setFrom(v)
              }}
            />
          </label>
          <label>
            ถึงวันที่
            <DatePicker
              value={to}
              onChange={(v) => {
                setPage(1)
                setTo(v)
              }}
              min={from}
            />
          </label>
          <label>
            ค้นหา
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="รหัสพนักงาน หรือชื่อ"
            />
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={offsiteOnly}
              onChange={(e) => {
                setPage(1)
                setOffsiteOnly(e.target.checked)
              }}
            />
            เฉพาะที่ลงจากนอกสถานที่
          </label>
          <button type="submit" disabled={loading}>
            ค้นหา
          </button>
        </form>

        {/* ค้างนาน = เครื่องคำนวณยังไม่เห็นรอยพวกนี้ ต้องเห็นได้ ไม่ใช่เงียบ */}
        {pending > 0 && (
          <p className="warn">
            มี <strong>{pending} รายการ</strong>ที่ยังไม่ถูกส่งเข้าเครื่องคำนวณเวลาทำงาน —
            ระบบจะลองใหม่เองตอนแอปเริ่มทำงานรอบถัดไป ถ้าค้างข้ามวันให้แจ้งผู้ดูแล
          </p>
        )}

        <SortBar
          sort={sort.sort}
          labels={{
            at: 'เวลา',
            empCode: 'พนักงาน',
            kind: 'เข้า/ออก',
            distance: 'ระยะห่าง',
            allowMode: 'สิทธิ์',
            pushed: 'ส่งเข้าระบบ',
          }}
          onClear={() => sort.setSort([])}
        />

        {rows === null ? (
          <p className="meta">กำลังโหลด…</p>
        ) : list.length === 0 ? (
          <p className="meta">
            ไม่มีการลงเวลาผ่าน LINE ในช่วงวันที่เลือก
            {offsiteOnly && ' (ที่ลงจากนอกสถานที่)'}
          </p>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <SortTh label="เวลา" sortKey="at" sort={sort.sort} onToggle={sort.toggle} className="nowrap" />
                  <SortTh label="พนักงาน" sortKey="empCode" sort={sort.sort} onToggle={sort.toggle} />
                  <SortTh label="เข้า/ออก" sortKey="kind" sort={sort.sort} onToggle={sort.toggle} />
                  <SortTh label="ลงจากที่ไหน" sortKey="distance" sort={sort.sort} onToggle={sort.toggle} />
                  <SortTh label="ผ่านเพราะ" sortKey="allowMode" sort={sort.sort} onToggle={sort.toggle} />
                  <th>รูป</th>
                  <SortTh label="ส่งเข้าระบบ" sortKey="pushed" sort={sort.sort} onToggle={sort.toggle} className="nowrap" />
                </tr>
              </thead>
              <tbody>
                {list.map((c) => {
                  const cancelled = c.cancelRec === 'Y'
                  return (
                    <tr key={c.seq} className={cancelled ? 'off' : undefined}>
                      <td className="nowrap">
                        {c.at.slice(0, 16)}
                        {cancelled && <div className="meta">ยกเลิกแล้ว</div>}
                      </td>
                      <td>
                        <strong>{c.empCode}</strong>
                        <div className="meta">{c.empName ?? '-'}</div>
                      </td>
                      <td>
                        <span className={c.kind === 'in' ? 'chip chip--ok' : 'chip'}>
                          {c.kind === 'in' ? 'เข้า' : 'ออก'}
                        </span>
                      </td>
                      <td>
                        <Where row={c} />
                      </td>
                      <td>
                        {c.allowReason ? (
                          <>
                            {REASON_LABEL[c.allowReason] ?? c.allowReason}
                            {c.allowMode === 'anywhere' && (
                              <div className="meta">แบบจากที่ใดก็ได้</div>
                            )}
                          </>
                        ) : (
                          <span className="meta">ไม่ได้บันทึกไว้</span>
                        )}
                      </td>
                      <td>
                        {c.hasPhoto ? (
                          <button type="button" className="ghost tiny" onClick={() => setPhoto(c)}>
                            ดูรูป
                          </button>
                        ) : (
                          <span className="meta">ไม่มี</span>
                        )}
                      </td>
                      <td className="nowrap">
                        {cancelled ? (
                          <span className="meta">ไม่ส่ง</span>
                        ) : c.pushed ? (
                          <span className="chip chip--ok">ส่งแล้ว</span>
                        ) : (
                          <span className="chip chip--warn">ยังไม่ส่ง</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {rows && (
          <Pager
            page={rows.page}
            pageSize={rows.pageSize}
            total={rows.total}
            loading={loading}
            onPage={setPage}
            unit="รายการ"
            onPageSize={(n) => {
              setPageSize(n)
              setPage(1)
            }}
          />
        )}
      </section>

      {/* ลอยทับหน้า (position: fixed) — วางท้าย JSX เพื่อไม่ให้คั่นระหว่างหัวเรื่องกับตาราง */}
      {photo && <PhotoBox row={photo} onClose={() => setPhoto(null)} />}
    </>
  )
}

/**
 * "ลงจากที่ไหน" — จุดที่คำเตือนเรื่องผู้บริหารเป็นรูปธรรม
 *
 * ระยะห่างคือคำตอบตรงที่สุดว่าคนนี้อยู่ที่ออฟฟิศจริงไหม แต่ต้องอ่านคู่กับ accuracy
 * เสมอ เพราะ GPS ในอาคารคลาดเคลื่อน ±20–65 ม. เป็นเรื่องปกติ (ดูกติกาใน README)
 */
function Where({ row }: { row: Checkin }) {
  const far = row.allowMode === 'anywhere' || row.siteCode === null

  return (
    <>
      {row.siteName ? (
        <>
          {row.siteName}
          <div className="meta">
            ห่าง {row.distanceM === null ? '—' : `${nf.format(row.distanceM)} ม.`}
            {row.accuracyM !== null && ` · ความแม่นยำ ±${row.accuracyM} ม.`}
          </div>
        </>
      ) : (
        <>
          <span className="chip chip--warn">นอกสถานที่</span>
          <div className="meta">
            ไม่ได้อยู่ในรัศมีของสถานที่ไหน
            {row.accuracyM !== null && ` · ความแม่นยำ ±${row.accuracyM} ม.`}
          </div>
        </>
      )}
      {row.lat !== null && row.lng !== null && (
        <div className="meta">
          <a
            href={`https://www.google.com/maps?q=${row.lat},${row.lng}`}
            target="_blank"
            rel="noreferrer"
          >
            ดูตำแหน่งบนแผนที่
          </a>
          {far && ' — ตรวจว่าอยู่ที่ไหนจริง'}
        </div>
      )}
    </>
  )
}

/**
 * ดูรูปเซลฟี่ — เป็น **modal ลอยทับหน้า** ไม่ใช่การ์ดในหน้า
 *
 * ⚠️ เคยทำเป็นการ์ดวางไว้เหนือตารางแล้วพัง: คนกดปุ่ม "ดูรูป" ในแถวที่เลื่อนลงมาแล้ว
 *    รูปไปโผล่บนสุดของหน้าซึ่งอยู่นอกจอ → เห็นเป็น "กดแล้วไม่มีอะไรเกิดขึ้น"
 *    ของที่เปิดจากการกดปุ่มในตารางต้องลอยทับตรงที่คนมองอยู่เสมอ
 *
 * ใช้ .drawer-scrim ตัวเดียวกับแผงพนักงาน — ฉากหลังกดปิดได้ และ index.css ใช้
 * `body:has(.drawer-scrim)` ล็อกการเลื่อนหน้าข้างหลังไว้ให้แล้ว
 *
 * ⚠️ ดึงด้วย fetch + blob ไม่ใช่ `<img src>` ตรง ๆ — รูปอยู่หลัง endpoint ที่ต้อง
 *    ยืนยันตัวตน (โฟลเดอร์ .data ถูกบล็อกจาก URL) การให้ `<img>` ยิงเองต้องพึ่ง
 *    ว่าเบราว์เซอร์จะแนบ cookie ข้าม origin ให้ ซึ่งขึ้นกับ SameSite ที่วันหนึ่ง
 *    อาจถูกเปลี่ยน · fetch(credentials:'include') คือทางเดียวกับที่ทั้งแอปใช้อยู่แล้ว
 */
function PhotoBox({ row, onClose }: { row: Checkin; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let objectUrl: string | null = null
    let alive = true

    fetch(`${API_BASE}${BASE}/checkins/${row.seq}/photo`, { credentials: 'include' })
      .then(async (res) => {
        if (!res.ok) throw new Error(`โหลดรูปไม่ได้ (HTTP ${res.status})`)
        const blob = await res.blob()
        if (!alive) return
        objectUrl = URL.createObjectURL(blob)
        setUrl(objectUrl)
      })
      .catch((err: unknown) => {
        if (alive) setError(err instanceof Error ? err.message : 'โหลดรูปไม่ได้')
      })

    return () => {
      alive = false
      // ไม่ revoke แล้วรูปจะค้างในหน่วยความจำจนกว่าจะปิดแท็บ
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [row.seq])

  // ปิดด้วย Esc — ของที่ลอยทับหน้าต้องปิดด้วยคีย์บอร์ดได้ ไม่ใช่ต้องเล็งปุ่มอย่างเดียว
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <>
      <button type="button" className="drawer-scrim" onClick={onClose} aria-label="ปิดรูป" />

      <div className="photo-modal" role="dialog" aria-modal="true" aria-label="รูปตอนลงเวลา">
        <header className="photo-modal__head">
          <div>
            <p className="drawer-title">
              {row.empCode} {row.empName ?? ''}
            </p>
            <p className="drawer-sub">
              {row.at.slice(0, 16)} · {row.kind === 'in' ? 'เข้า' : 'ออก'}
              {row.photoBytes ? ` · ${Math.round(row.photoBytes / 1024)} KB` : ''}
            </p>
          </div>
          <button type="button" className="drawer-close" onClick={onClose} aria-label="ปิด">
            ✕
          </button>
        </header>

        <div className="photo-modal__body">
          {error && <p className="error">{error}</p>}
          {!url && !error && <p className="meta">กำลังโหลดรูป…</p>}
          {url && <img className="checkin-photo" src={url} alt={`รูปตอนลงเวลาของ ${row.empCode}`} />}
          <p className="meta">
            ถ่ายด้วยกล้องหน้าตอนกดลงเวลา — ใช้ยืนยันว่าเป็นเจ้าตัวจริง คู่กับพิกัดและเวลา
          </p>
        </div>
      </div>
    </>
  )
}
